import React, { FC, ReactNode } from 'react';
import styled from 'styled-components';
import * as tokens from '@utmost.co/utmost-design-tokens';
import { Chip } from '../Chip/Chip';
import { statusChipColors, StatusChipType } from '../Chip/StatusChip';
import { IconName, SystemIcon } from '../Icon';
import { Uds } from '../../types';

type Props = Uds<{
  onClick: (e: React.MouseEvent) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  label: ReactNode;
  selected: boolean;
  iconName?: IconName;
  variant?: 'outlined' | 'default';
  size?: 'small' | 'medium';
  className?: string;
  disabled?: boolean;
}>;

const getChipType = (disabled: boolean | undefined, selected: boolean): StatusChipType => {
  if (disabled) return selected ? StatusChipType.DISABLED_SELECTED : StatusChipType.DISABLED;
  return selected ? StatusChipType.LIGHT_BLUE : StatusChipType.GREY;
};

export const ChipSelect: FC<React.PropsWithChildren<Props>> = ({
  dataTest,
  onClick,
  onKeyDown,
  label,
  selected,
  iconName,
  className,
  disabled,
  variant,
  size = 'medium'
}) => {
  const chipType = getChipType(disabled, selected);
  return (
    <StyledChip
      data-test={dataTest || 'chip-select'}
      dataTestLabel={typeof label === 'string' ? label : undefined}
      className={`${className ?? ''}${disabled ? ' disabled' : ''}`}
      $type={chipType}
      $variant={variant}
      $size={size}
      label={
        <StyledLabel $selected={selected || !!iconName}>
          {iconName && <StyledIcon name={iconName} $selected={!selected} />}
          <StyledIcon dataTest="select-chip-check" size={size} $selected={selected} name="check" />
          <span className="label-text" data-test="select-chip-text" data-test-selected={selected ? 'true' : 'false'}>
            {label}
          </span>
        </StyledLabel>
      }
      onClick={disabled ? noop : onClick}
      onKeyDown={disabled ? noop : onKeyDown}
      tabIndex={0}
      variant="default"
    />
  );
};

function noop() {}

const StyledIcon = styled(SystemIcon)<{ $selected: boolean }>`
  opacity: ${({ $selected }) => ($selected ? 1 : 0)};
  width: ${({ $selected }) => ($selected ? 'auto' : 0)};
  transition: width 250ms;
`;

const StyledLabel = styled.span<{ $selected: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${({ $selected, theme }) => ($selected ? theme.spacingSize.s : 0)};
`;

const StyledChip = styled(Chip)<{ $type: StatusChipType; $variant?: 'outlined' | 'default'; $size: Props['size'] }>`
  background-color: ${({ $type, $variant }) => {
    if ($variant === 'outlined' && $type === StatusChipType.GREY) return 'transparent';
    return statusChipColors[$type].backgroundColor;
  }};
  border: ${({ $type, $variant }) => {
    if ($variant === 'outlined' && $type === StatusChipType.GREY) {
      return `1px solid ${statusChipColors.GREY.color}`;
    }
    return undefined;
  }};
  opacity: ${({ $type }) => (statusChipColors[$type] as (typeof statusChipColors)['DISABLED_SELECTED']).opacity};
  transition: background-color 250ms;
  border-radius: 16px;
  line-height: ${({ $size }) => ($size === 'small' ? '20px' : '24px')};
  cursor: pointer;
  padding: ${({ theme, $size }) => ($size === 'small' ? '1px' : theme.spacingSize.s)};

  &:focus {
    background: ${({ $type }) =>
      $type === StatusChipType.LIGHT_BLUE ? tokens.colorSecondaryContainer : `rgba(0, 0, 0, 0.18)`};
  }

  span {
    color: ${({ $type }) => statusChipColors[$type].color};
  }
    
  .text {
      padding-inline: ${({ $size, theme }) => ($size === 'small' ? theme.spacingSize.s : '0')};
  }

  svg {
    color: ${({ theme }) => theme.paletteNeutralVariant20}!important;
  }

  .label-text {
    padding: 0 ${({ theme }) => theme.spacingSize.s};

    .MuiTypography-root {
      color: ${({ theme }) => theme.paletteNeutralVariant20};
    }
  }
  &.disabled {
      cursor: default;
      pointer-events: none;
  }
`;
